import { useUnderlyingRoundPhases } from "@client/hooks/use-round-phase";
import {
  useImpersonateContext,
  useRoundAdmin,
} from "@client/ImpersonateContext";
import {
  adminNavItemIsActive,
  buildRoundAdminOrFunderNavItems,
  isRoundAdminNavItemVisible,
} from "@client/utils/roundAdminNav";
import { cn } from "@sfc/ui";
import { type ReactNode } from "react";
import { Link, useLocation } from "wouter";

export function RoundAdminNavbar({
  roundId,
  children,
}: {
  roundId: string;
  children: ReactNode;
}) {
  const [pathname] = useLocation();
  const currentPhases = useUnderlyingRoundPhases();
  const { isAdmin, isFunder } = useRoundAdmin();
  const { value } = useImpersonateContext();

  const visibleItems = buildRoundAdminOrFunderNavItems(
    roundId,
    true,
    value?.round.isSfcRound ?? true,
  ).filter((item) =>
    isRoundAdminNavItemVisible(item, {
      isAdmin,
      isFunder,
      currentPhases,
    }),
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="lg:hidden overflow-x-auto border-b shrink-0">
        <div className="flex items-center gap-2 px-4 py-3">
          {visibleItems.map(({ label, href, icon: Icon }) => {
            const isActive = adminNavItemIsActive(
              pathname,
              roundId,
              href,
              true,
            );
            return (
              <Link
                key={href}
                href={href}
                className={cn(
                  "flex items-center gap-2 h-9 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors",
                  isActive
                    ? "bg-accent-default text-white"
                    : "text-text-primary hover:bg-surface-hover bg-surface-base border border-border-subtle",
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            );
          })}
        </div>
      </div>
      <div className="flex-1 flex overflow-hidden">
        <aside className="hidden lg:flex flex-col w-60 shrink-0 overflow-y-auto">
          <nav className="p-3 space-y-0.5">
            {visibleItems.map(
              ({ label, href, icon: Icon, separatorBefore }) => {
                const isActive = adminNavItemIsActive(
                  pathname,
                  roundId,
                  href,
                  true,
                );
                return (
                  <div key={href}>
                    {separatorBefore && (
                      <hr className="my-2 border-border-subtle" />
                    )}
                    <Link
                      href={href}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                        isActive
                          ? "bg-surface-elevated text-text-primary border border-border-subtle shadow-sm"
                          : "text-text-secondary hover:text-text-primary hover:bg-surface-hover",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span>{label}</span>
                    </Link>
                  </div>
                );
              },
            )}
          </nav>
        </aside>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
